/**
 * Message Parser Service
 * Converts Wix API messages to UI format with complete content parsing
 */

interface ParsedMessage {
  id: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  _createdDate: Date;
  isFromCustomer: boolean;
  avatarUrl?: string;
  contentType?: string; // FORM, TEMPLATE, TEXT, etc.
  content?: {
    form?: {
      fields?: Array<{ name: string; value: string }>;
    };
    [key: string]: unknown;
  };
  formTitle?: string; // Title for FORM messages
  media?: Array<{ url?: string; type: 'image' | 'video' | 'file' | 'audio' | 'icon'; name?: string; iconName?: string; duration?: number }>;
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  wixCallRecordingBadge?: { text: string; icon?: string; iconUrl?: string }; // Wix call recording badge (minimal display)
  channelBadge?: { text: string; iconUrl?: string }; // Channel badge from Wix (e.g., Gmail, Outlook logos)
  subject?: string; // Email subject — the Wix message title of an email synced into Inbox
  clientMessageId?: string; // The app's own id for a message it sent (emails) — matches the saved message to the pending one
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
  sender?: Record<string, unknown>;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  sourceChannelId?: string; // Wix channel ID for inbound (where message came FROM)
  targetChannelIds?: string[]; // Wix channel IDs for outbound routing
}

/**
 * Determine message channel from badges and sourceChannelId
 * Preserves specific email provider information (gmail, outlook, yahoo, etc.)
 */
function parseChannel(msgObj: Record<string, unknown>): 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email' {
  const badges = msgObj.badges as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(badges) && badges.length > 0) {
    const badgeText = String(badges[0]?.text || '').toLowerCase().trim();
    if (badgeText.includes('sms')) return 'sms';
    if (badgeText.includes('gmail')) return 'gmail';
    if (badgeText.includes('outlook')) return 'outlook';
    if (badgeText.includes('yahoo')) return 'yahoo';
    if (badgeText.includes('email')) return 'email';
  }

  const sourceChannelId = String(msgObj.sourceChannelId || '').toLowerCase().trim();
  if (sourceChannelId.includes('sms')) return 'sms';
  if (sourceChannelId.includes('gmail')) return 'gmail';
  if (sourceChannelId.includes('outlook')) return 'outlook';
  if (sourceChannelId.includes('yahoo')) return 'yahoo';
  if (sourceChannelId.includes('email')) return 'email';

  // Also check sourceChannelIds object if sourceChannelId string is not set
  const sourceChannelIds = msgObj.sourceChannelIds as Record<string, unknown> | undefined;
  if (sourceChannelIds) {
    const channelType = String(sourceChannelIds.channelType || '').toUpperCase();
    if (channelType === 'SMS' || channelType === 'PHONE') return 'sms';
    if (channelType === 'EMAIL') {
      // Try to identify the specific email provider
      const email = String(sourceChannelIds.email || '').toLowerCase();
      if (email.includes('gmail')) return 'gmail';
      if (email.includes('outlook') || email.includes('hotmail') || email.includes('live.com')) return 'outlook';
      if (email.includes('yahoo')) return 'yahoo';
      if (email) return 'other-email';
      return 'email';
    }
    
    // Check for email or phone properties
    if (sourceChannelIds.email) {
      const email = String(sourceChannelIds.email).toLowerCase();
      if (email.includes('gmail')) return 'gmail';
      if (email.includes('outlook') || email.includes('hotmail') || email.includes('live.com')) return 'outlook';
      if (email.includes('yahoo')) return 'yahoo';
      return 'other-email';
    }
    if (sourceChannelIds.phoneNumber) return 'sms';
  }

  return 'chat';
}

/**
 * The link to a file in the site's Wix Media Manager, from its Media id. Wix returns an empty `url` on a message
 * that was just created through the API (email attachments are added that way), and a link it does give can lapse —
 * but an uploaded file is public and permanent, so its address is always known from the id.
 */
function wixMediaUrl(id: string, kind: 'image' | 'file'): string {
  if (!id || id.includes(':')) return ''; // a Wix media URI (wix:image://…) is not a bare id
  if (kind === 'image') return `https://static.wixstatic.com/media/${id}`;
  const siteId = import.meta.env.VITE_WIX_SITE_ID as string | undefined;
  return siteId ? `https://${siteId}.usrfiles.com/ugd/${id}` : '';
}

/**
 * Extract media from various content structures
 * Returns: { media, wixCallRecordingBadge }
 */
function extractMedia(contentObj: Record<string, unknown> | undefined, msgObj?: Record<string, unknown>): { media: Array<{ url?: string; type: 'image' | 'video' | 'file' | 'audio' | 'icon'; name?: string; iconName?: string; duration?: number }>; wixCallRecordingBadge?: { text: string; icon?: string; iconUrl?: string } } {
  const media: Array<{ url?: string; type: 'image' | 'video' | 'file' | 'audio' | 'icon'; name?: string; iconName?: string; duration?: number }> = [];
  let wixCallRecordingBadge: { text: string; icon?: string; iconUrl?: string } | undefined;

  if (!contentObj) return { media, wixCallRecordingBadge };

  // Extract from content.basic.items
  const basicObj = contentObj.basic as Record<string, unknown> | undefined;
  if (basicObj && Array.isArray(basicObj.items)) {
    (basicObj.items as Array<Record<string, unknown>>).forEach((item) => {
      if (item.text && typeof item.text === 'string') {
        // Already handled as text
      }
      if (item.image) {
        const imageData = item.image as Record<string, unknown>;
        const url = String(imageData.url || '') || wixMediaUrl(String(imageData.id || ''), 'image');
        const filename = String(imageData.filename || 'image.jpg');
        if (url) media.push({ url, type: 'image', name: filename });
      }
      if (item.video) {
        const videoData = item.video as Record<string, unknown>;
        const url = String(videoData.url || '');
        const filename = String(videoData.filename || 'video.mp4');
        if (url) media.push({ url, type: 'video', name: filename });
      }
      if (item.file) {
        const fileData = item.file as Record<string, unknown>;
        const url = String(fileData.url || '') || wixMediaUrl(String(fileData.id || ''), 'file');
        const filename = String(fileData.filename || 'file');
        if (url) media.push({ url, type: 'file', name: filename });
      }
    });
  }

  // Extract from root attachments
  if (Array.isArray(contentObj.attachments)) {
    (contentObj.attachments as Array<Record<string, unknown>>).forEach((att) => {
      const url = att.url ? String(att.url) : undefined;
      if (url && !media.some(m => m.url === url)) {
        const filename = String(att.name || att.filename || 'attachment');
        const mimeType = String(att.mimeType || att.type || '').toLowerCase();
        let type: 'image' | 'video' | 'file' | 'audio' = 'file';
        if (mimeType.includes('image')) type = 'image';
        else if (mimeType.includes('video')) type = 'video';
        else if (mimeType.includes('audio')) type = 'audio';
        media.push({ url, type, name: filename });
      }
    });
  }

  // Extract from rich content
  const richObj = contentObj.rich as Record<string, unknown> | undefined;
  if (richObj?.items && Array.isArray(richObj.items)) {
    (richObj.items as Array<Record<string, unknown>>).forEach((item) => {
      if (item.file) {
        const fileData = item.file as Record<string, unknown>;
        const url = String(fileData.url || '');
        if (url && !media.some(m => m.url === url)) {
          media.push({ url, type: 'file', name: String(fileData.filename || 'file') });
        }
      }
      if (item.image) {
        const imageData = item.image as Record<string, unknown>;
        const url = String(imageData.url || '');
        if (url && !media.some(m => m.url === url)) {
          media.push({ url, type: 'image', name: String(imageData.filename || 'image.jpg') });
        }
      }
    });
  }

  // Extract from email attachments
  const emailObj = contentObj.email as Record<string, unknown> | undefined;
  if (emailObj?.attachments && Array.isArray(emailObj.attachments)) {
    (emailObj.attachments as Array<Record<string, unknown>>).forEach((att) => {
      const url = att.url ? String(att.url) : undefined;
      if (url && !media.some(m => m.url === url)) {
        const filename = String(att.name || att.filename || 'attachment');
        const mimeType = String(att.mimeType || att.type || '').toLowerCase();
        let type: 'image' | 'video' | 'file' | 'audio' = 'file';
        if (mimeType.includes('image')) type = 'image';
        else if (mimeType.includes('video')) type = 'video';
        else if (mimeType.includes('audio')) type = 'audio';
        media.push({ url, type, name: filename });
      }
    });
  }

  // Extract Wix call recording badge (message-level or content-level)
  const msgBadges = msgObj && Array.isArray((msgObj as Record<string, unknown>).badges) ? ((msgObj as Record<string, unknown>).badges as Array<Record<string, unknown>>) : [];
  const contentBadges = Array.isArray(contentObj.badges) ? (contentObj.badges as Array<Record<string, unknown>>) : [];
  const allBadges = [...msgBadges, ...contentBadges];
  
  for (const badge of allBadges) {
    if (badge.text === 'Call Recording') {
      wixCallRecordingBadge = {
        text: String(badge.text),
        icon: badge.icon ? String(badge.icon) : undefined,
        iconUrl: badge.iconUrl ? String(badge.iconUrl) : undefined
      };
      console.log('🎙️ [WIX] Call Recording Badge Found:', wixCallRecordingBadge);
      break; // Only one call recording badge per message
    }
  }

  return { media, wixCallRecordingBadge };
}

/**
 * Extract text from basic items
 */
function extractBasicText(contentObj: Record<string, unknown> | undefined): string {
  const textParts: string[] = [];
  const basicObj = (contentObj as Record<string, unknown>)?.basic as Record<string, unknown> | undefined;

  if (basicObj && Array.isArray(basicObj.items)) {
    (basicObj.items as Array<Record<string, unknown>>).forEach((item) => {
      if (item.text && typeof item.text === 'string') {
        textParts.push(item.text);
      }
    });
  }

  return textParts.join('\n');
}

/**
 * Parse MINIMAL content type
 */
function parseMinimalContent(contentObj: Record<string, unknown> | undefined): { text: string; media: ParsedMessage['media'] } {
  const minimalObj = contentObj?.minimal as Record<string, unknown> | undefined;
  const text = minimalObj?.text ? String(minimalObj.text) : '';
  const icon = minimalObj?.icon ? String(minimalObj.icon) : '';
  const iconUrl = minimalObj?.iconUrl ? String(minimalObj.iconUrl) : '';
  const media: ParsedMessage['media'] = [];

  const iconLower = icon.toLowerCase();
  const iconUrlLower = iconUrl.toLowerCase();
  const textLower = text.toLowerCase();

  // For "Sent Quote" messages, use quote icon instead of the image URL
  if (textLower.includes('sent quote')) {
    media?.push({ type: 'icon', iconName: 'quote', name: 'Quote' });
  } else if (textLower.includes('contact was merged')) {
    // "Contact was merged" should use merge icon, not image
    media?.push({ type: 'icon', iconName: 'merge', name: 'Contact Merged' });
  } else {
    const shouldUseIcon = iconLower.includes('email') || iconLower.includes('mail') || iconLower.includes('invoice') ||
      iconLower.includes('call') || iconLower.includes('phone') || iconLower.includes('member') ||
      iconUrlLower.includes('merge.png');

    if (icon && shouldUseIcon) {
      media?.push({ type: 'icon', iconName: icon, name: 'Activity Icon' });
    } else if (textLower.includes('subscribed') || textLower.includes('unsubscribed') || textLower.includes('subscription')) {
      media?.push({ type: 'icon', iconName: 'subscribe', name: 'Activity Icon' });
    } else if (iconUrl) {
      media?.push({ url: iconUrl, type: 'image', name: 'Activity Icon' });
    }
  }

  return { text, media };
}

/**
 * Parse TEMPLATE content type
 */
function parseTemplateContent(contentObj: Record<string, unknown> | undefined): { 
  text: string; 
  media: ParsedMessage['media'];
  invoicePaidData?: ParsedMessage['invoicePaidData'];
  newOrderData?: ParsedMessage['newOrderData'];
} {
  const templateObj = contentObj?.template as Record<string, unknown> | undefined;
  const title = templateObj?.title ? String(templateObj.title) : '';
  const textLines = templateObj?.textLines as string[] | undefined;
  const buttons = templateObj?.buttons as Array<Record<string, unknown>> | undefined;
  const imageUrl = templateObj?.imageUrl ? String(templateObj.imageUrl) : '';
  const icon = templateObj?.icon ? String(templateObj.icon) : '';
  const media: ParsedMessage['media'] = [];

  const parts: string[] = [];
  if (title) parts.push(title);
  if (textLines && textLines.length > 0) parts.push(textLines.join('\n'));
  if (buttons && buttons.length > 0) {
    const buttonLabels = buttons.map(b => b.label ? `[${b.label}]` : '').filter(Boolean).join(' ');
    if (buttonLabels) parts.push(buttonLabels);
  }

  // Format buttons for card rendering
  const formattedButtons = buttons?.map(b => ({
    label: b.label ? String(b.label) : '',
    launchUrl: b.launchUrl ? String(b.launchUrl) : ''
  }));

  let invoicePaidData: ParsedMessage['invoicePaidData'] | undefined;
  let newOrderData: ParsedMessage['newOrderData'] | undefined;

  const iconLower = icon.toLowerCase();
  const titleLower = title.toLowerCase();

  // Detect message type and set appropriate data object
  if (iconLower.includes('invoice.paid')) {
    // Invoice Paid Card
    invoicePaidData = {
      title,
      textLines,
      buttons: formattedButtons,
      icon
    };
    if (imageUrl) {
      media.push({ url: imageUrl, type: 'image', name: 'Invoice' });
    }
  } else if (iconLower.includes('quote') || titleLower.includes('quote')) {
    // Quote Card (render like Order)
    newOrderData = {
      title,
      textLines,
      buttons: formattedButtons,
      imageUrl
    };
    if (imageUrl) {
      media.push({ url: imageUrl, type: 'image', name: 'Quote' });
    }
  } else if ((!icon || icon === '') && imageUrl && (titleLower.includes('order') || titleLower.includes('new order') || titleLower.includes('booking') || titleLower.includes('new booking'))) {
    // New Order/Booking Card (no icon, has imageUrl, title contains 'order' or 'booking')
    newOrderData = {
      title,
      textLines,
      buttons: formattedButtons,
      imageUrl
    };
    media.push({ url: imageUrl, type: 'image', name: 'Order/Booking' });
  } else if (imageUrl && (titleLower.includes('order') || titleLower.includes('new order') || titleLower.includes('booking') || titleLower.includes('new booking'))) {
    // Order/Booking without imageUrl or with icon - treat as order/booking card anyway
    newOrderData = {
      title,
      textLines,
      buttons: formattedButtons,
      imageUrl: imageUrl || undefined
    };
    if (imageUrl) {
      media.push({ url: imageUrl, type: 'image', name: 'Order/Booking' });
    }
  } else if (imageUrl) {
    // Generic image
    media.push({ url: imageUrl, type: 'image', name: 'Template Image' });
  }

  return { text: parts.join('\n'), media, invoicePaidData, newOrderData };
}

/**
 * Main message parser - converts Wix message to UI format
 */
export function parseWixMessage(msg: unknown, contactName: string): ParsedMessage {
  const msgObj = msg as Record<string, unknown>;

  // Determine if from customer
  const direction = String(msgObj.direction || '').toUpperCase().trim();
  const senderType = msgObj.senderType as string | undefined;
  const senderStaffName = msgObj.senderStaffName as string | undefined;
  
  // If direction says PARTICIPANT_TO_BUSINESS but senderType indicates staff, this is a data inconsistency
  // Trust senderType/senderStaffName over direction (they're enriched fields)
  const isFromCustomer = 
    direction === 'PARTICIPANT_TO_BUSINESS' && 
    senderType !== 'staff' && 
    !senderStaffName;

  // Get sender info
  const senderObj = msgObj.sender as Record<string, unknown> | undefined;
  let senderName = isFromCustomer ? contactName : (senderObj?.name ? String(senderObj.name) : 'RS Maids');

  // senderStaffAvatar already extracted above
  const senderStaffAvatar = msgObj.senderStaffAvatar as string | undefined;
  
  // Try to find message text from various locations
  let staffMessageText = msgObj.text as string | undefined;
  if (!staffMessageText) {
    // Try fullText field
    staffMessageText = msgObj.fullText as string | undefined;
  }
  if (!staffMessageText) {
    // Try extracting from content structure
    const contentObj = msgObj.content as Record<string, unknown> | undefined;
    const basicObj = contentObj?.basic as Record<string, unknown> | undefined;
    const items = basicObj?.items as Array<Record<string, unknown>> | undefined;
    if (items && items.length > 0 && items[0].text) {
      staffMessageText = items[0].text as string | undefined;
    }
  }

  // Extract embedded metadata (e.g., from workaround messages like "Helix")
  // This contains the actual staff info when non-Wix members send as staff
  const metadata = msgObj.metadata as Record<string, unknown> | undefined;
  const extractedStaffInfo = metadata?.extractedStaffInfo as Record<string, unknown> | undefined;

  // CRITICAL: Extract staff name from message text if it starts with "STAFFNAME: " pattern
  // Historical "Helix: test message" messages have the staff name embedded in text
  // BUT: Exclude business notifications like "Sent Invoice: #0001086" - these are not staff names
  let extractedFromText: string | undefined;
  if (staffMessageText && staffMessageText.includes(':') && !isFromCustomer) {
    const textMatch = staffMessageText.match(/^([A-Za-z\s]+):\s/);
    if (textMatch && textMatch[1]) {
      const potentialStaffName = textMatch[1].trim();
      
      // EXCLUDE: Known business notification markers (not actual staff names)
      const businessNotifications = ['Sent Invoice'];
      if (!businessNotifications.includes(potentialStaffName)) {
        extractedFromText = potentialStaffName;
      }
    }
  }



  // CRITICAL: For historical messages without senderStaffName, infer it from sender context
  // Historical "Helix Tester" messages lack senderStaffName/senderType but have meaningful sender name
  let inferredSenderStaffName = senderStaffName || extractedFromText;
  let inferredSenderType = senderType;

  if (!inferredSenderStaffName && !isFromCustomer && senderName && senderName !== 'RS Maids') {
    // Historical business message with a specific sender name - infer as staff
    // (Exception: if wixUserId is business account ID, keep as "RS Maids")
    const wixUserId = senderObj?.wixUserId ? String(senderObj.wixUserId) : undefined;
    const isBusinessAccount = wixUserId === 'efff1c9b-f79b-400c-b76d-e30e572bb2fc';
    
    if (!isBusinessAccount) {
      // This is a named staff member, not the generic business account
      inferredSenderStaffName = senderName;
      inferredSenderType = 'staff';
    }
  }

  // Use Firestore enriched staff name if available (even if senderType not set)
  // This handles old cached messages that have senderStaffName but no senderType
  if (inferredSenderStaffName && inferredSenderStaffName !== 'RS Maids' && !isFromCustomer) {
    senderName = inferredSenderStaffName;
  } else if (inferredSenderType === 'staff' && inferredSenderStaffName) {
    senderName = inferredSenderStaffName;
  }

  // Parse content
  const contentObj = msgObj.content as Record<string, unknown> | undefined;
  const contentType = contentObj?.contentType as string | undefined;
  const channel = parseChannel(msgObj);

  // Extract form title - it can be at content.form.title or content.previewText
  let formTitle: string | undefined;
  if (contentType === 'FORM') {
    const formObj = contentObj?.form as Record<string, unknown> | undefined;
    formTitle = (formObj?.title as string) || (contentObj?.previewText as string);
  }

  // Extract media and call recording badge
  const mediaResult = extractMedia(contentObj, msgObj);
  let text = '';
  let media = mediaResult.media;
  let wixCallRecordingBadge = mediaResult.wixCallRecordingBadge;
  
  // Extract channel badge (Gmail, Outlook, Yahoo logos from Wix)
  let channelBadge: ParsedMessage['channelBadge'] | undefined;
  const badges = msgObj.badges as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(badges) && badges.length > 0) {
    const firstBadge = badges[0];
    if (firstBadge.text && firstBadge.text !== 'Call Recording') {
      channelBadge = {
        text: String(firstBadge.text || ''),
        iconUrl: firstBadge.iconUrl ? String(firstBadge.iconUrl) : undefined
      };
      console.log(`🏷️ Channel badge found: ${channelBadge.text}, iconUrl: ${channelBadge.iconUrl ? 'SET' : 'NONE'}`);
    }
  }
  
  let invoicePaidData: ParsedMessage['invoicePaidData'] | undefined;
  let newOrderData: ParsedMessage['newOrderData'] | undefined;

  if (contentType === 'MINIMAL') {
    const result = parseMinimalContent(contentObj);
    text = result.text;
    media = [...(media || []), ...(result.media || [])];
  } else if (contentType === 'TEMPLATE') {
    const result = parseTemplateContent(contentObj);
    text = result.text;
    media = [...(media || []), ...(result.media || [])];
    invoicePaidData = result.invoicePaidData;
    newOrderData = result.newOrderData;
  } else {
    text = extractBasicText(contentObj);
  }

  // Use clean text if available
  if (senderType === 'staff' && staffMessageText) {
    text = staffMessageText;
  }

  // CRITICAL: If we extracted a staff name from the message text (e.g., "Helix: message"),
  // remove that prefix from the displayed text so it only shows the message content
  // NOTE: We should NOT strip "Sent Invoice:" because it's not a staff name (filtered above)
  if (extractedFromText && text && text.startsWith(extractedFromText + ':')) {
    // Remove the "Helix: " prefix
    text = text.replace(new RegExp(`^${extractedFromText}:\\s*`), '');
  }

  const parseDate = (v: unknown): Date | null => {
    if (v == null) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === 'number' && v > 0) return new Date(v);
    if (typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (typeof o.toDate === 'function') return (o as { toDate(): Date }).toDate();
      if (typeof o._seconds === 'number') return new Date(o._seconds * 1000);
      if (typeof o.seconds === 'number') return new Date(o.seconds * 1000);
      // MongoDB $date or Firebase protobuf { value: "ISO" }
      const inner = o.$date ?? o.value;
      if (inner != null) return parseDate(inner);
    }
    if (typeof v === 'string' && v) {
      const parsed = new Date(v);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };
  const _createdDate =
    parseDate(msgObj._createdDate) ??
    parseDate(msgObj.createdDate) ??
    parseDate(msgObj.timestamp) ??
    new Date();

  // Get avatar - prioritize Firestore enriched avatar
  let avatarUrl = senderStaffAvatar;
  if (!avatarUrl && !isFromCustomer) {
    // Try to get from sender object if no Firestore avatar
    const senderAvatar = senderObj?.avatar as Record<string, unknown> | undefined;
    avatarUrl = senderAvatar?.url ? String(senderAvatar.url) : undefined;
  }

  // Use Firestore auto-generated document ID for React list rendering
  // This is guaranteed to be unique and set by firestoreCacheService
  // Fallback to _id or id from message if somehow missing
  let messageId = String(msgObj.id || msgObj._id || '');
  if (!messageId) {
    // Should rarely happen - just in case, use timestamp + random
    messageId = `msg_${_createdDate.getTime()}_${Math.random().toString(16).slice(2, 8)}`;
    console.warn(`⚠️ [messageParser] Message missing id/wixId, generated: ${messageId}`);
  }

  return {
    id: messageId,
    from: senderName,
    text,
    channel,
    _createdDate,
    isFromCustomer,
    avatarUrl,
    contentType: contentType && contentType !== 'BASIC' ? contentType : undefined,
    content: contentObj as Record<string, unknown>,
    formTitle,
    media: media && media.length > 0 ? media : undefined,
    invoicePaidData,
    newOrderData,
    wixCallRecordingBadge,
    channelBadge,
    // An email synced into Inbox carries its subject as the message title (Wix shows it bold above the text)
    subject: channel === 'email' && typeof contentObj?.title === 'string' && contentObj.title ? contentObj.title : undefined,
    clientMessageId: msgObj.clientMessageId ? String(msgObj.clientMessageId) : undefined,
    senderStaffName: inferredSenderStaffName || senderStaffName,
    senderStaffAvatar,
    senderType: inferredSenderType || senderType,
    senderStaffId: msgObj.senderStaffId ? String(msgObj.senderStaffId) : undefined,
    sender: senderObj,
    // Embed metadata for enrichment (e.g., actual staff info from workaround messages)
    extractedStaffInfo: extractedStaffInfo ? {
      staffName: extractedStaffInfo.staffName ? String(extractedStaffInfo.staffName) : '',
      avatarUrl: extractedStaffInfo.avatarUrl ? String(extractedStaffInfo.avatarUrl) : undefined
    } : undefined,
    // Wix channel ID for inbound (where message came FROM) - used for display/badges
    sourceChannelId: msgObj.sourceChannelId ? String(msgObj.sourceChannelId) : undefined,
    // Wix channel IDs for outbound routing (where to SEND TO)
    targetChannelIds: Array.isArray(msgObj.targetChannelIds) ? (msgObj.targetChannelIds as string[]) : undefined
  };
}
